import getLocal from '@/app/utils/getLocal';
import gtm from '@/utils/gtm';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import styles from './index.module.scss';

interface TabItemProps {
    /**
     * 对应 activeKey
     */
    tabValue: string;
    /**
     * 选项卡头显示文字
     */
    tabLabel: React.ReactNode;
    /**
     * 样式名
     */
    className?: string;
}

interface TabsProps {
    tabs: TabItemProps[];
    gtmTitle?: string;
    className?: string;
    activeTab?: string;
    activeIndex: number;
    onChange: (tab: TabItemProps, index: number) => void;
}

const Tabs: React.FC<TabsProps> = ({ tabs, gtmTitle, className, activeTab, activeIndex, onChange }) => {
    const { locale } = getLocal();

    const scrollRef = useRef<HTMLDivElement>(null);
    const scrollItemRef = useRef<HTMLDivElement[]>([]);

    function tabOnChange(e: React.MouseEvent<HTMLDivElement>, tab: TabItemProps, index: number) {
        onChange(tab, index);

        scrollToView(index);
        gtm.push({
            event: 'uaEvent',
            event_name: 'tab_click',
            list_name: gtmTitle,
            button_name: tab.tabLabel,
            __btn_id1: `${className} .tab-${index + 1}`,
            __btn_id2: tab.tabLabel,
            __btn_type: 'text',
        });
    }

    useEffect(() => {
        scrollToView(activeIndex);
    }, [activeIndex]);

    function scrollToView(index: number) {
        if (!scrollRef.current || !scrollItemRef.current[index]) return;

        const parentRect = scrollRef.current.getBoundingClientRect();
        const elemRect = scrollItemRef.current[index].getBoundingClientRect();

        let scrollLeft: number;

        const padding = window.innerWidth > 1024 ? 0 : window.innerWidth > 768 ? 48 : 24;
        // 如果是第一个item，滚动到最左边
        if (index === 0) {
            scrollLeft = elemRect.left - parentRect.left - padding;
        }
        // 如果是最后一个item，滚动到最右边
        else if (index === tabs.length - 1) {
            scrollLeft = elemRect.right - parentRect.right + padding;
        }
        // 其他情况居中显示
        else {
            scrollLeft = elemRect.left - parentRect.left - parentRect.width / 2 + elemRect.width / 2;
        }

        scrollRef.current.scrollBy({
            left: scrollLeft,
            behavior: 'smooth',
        });
    }

    return (
        <div ref={scrollRef} className={classNames(styles.Tabs, className)}>
            <div className="tab-box">
                {tabs.map((tab, index) => (
                    <div
                        ref={(node: any) => (scrollItemRef.current[index] = node)}
                        className={classNames(
                            'tab-item',
                            'GtmModuleData-btn-text',
                            `tab-${index + 1}`,
                            tab.tabValue === activeTab || index === activeIndex ? 'tab-item__active' : '',
                        )}
                        key={tab.tabValue}
                        data-btn-id={tab.tabLabel}
                        onClick={(e) => tabOnChange(e, tab, index)}
                    >
                        {tab.tabLabel}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Tabs;
